/**
 * !! What exactly is "enrichedFields" data? !!
 *
 * The enrichedFields data refers to the fields data that
 * has been made aware of and infused with other data (like farms and crops).
 *
 * These hooks are meant for more general use than the basicFields hooks.
 *
 * These hooks are to be used in places where fields data enriched
 * with its dependent data (like farms and crops) is needed.
 *
 */

import { AxiosError } from "axios";

import { FieldsDataEnvelope } from "../../../models/fields";
import {
  QueryStatus,
  QueryEnvelope,
  EnsuredQueryEnvelope,
} from "../../../services/query";

import * as basicFarmsHooks from "../farms/basicFarms";
import * as cropsHooks from "../crops";
import * as fieldCropsHooks from "../fieldCrops";
import * as fieldEnrollmentsHooks from "../fieldEnrollments";

import * as basicFieldsHooks from "./basicFields";
import { useSetFieldsGlobalActivityAndStatus } from "./fields.globalStatus";
import {
  computeCombinedStatus,
  computeCombinedError,
  computeCombinedData,
  computeEnvelopeOfEnsuredState,
  computeEnvelopeOfEnsuredStateWithData,
  useHandleStatusForGlobalStatus,
} from "./enrichedFields.helpers";
import { fieldsAccessError } from "./fields.helpers";

export { fieldsAccessError };

export * from "./fields.invalidate";
export * from "./fields.mutations";

// #####################
// ## useFieldsStatus ##
// #####################

export type ResultOfUseFieldsStatus = QueryStatus;
export type UseFieldsStatus = () => ResultOfUseFieldsStatus;

/**
 * The returned value of this hook will contain the status
 * of the fetching of the fields data.
 */
export const useFieldsStatus: UseFieldsStatus = () => {
  const basicFieldsStatus = basicFieldsHooks.useBasicFieldsStatus();
  const basicFarmsStatus = basicFarmsHooks.useBasicFarmsStatus();
  const cropsStatus = cropsHooks.useCropsStatus();
  const fieldCropsStatus = fieldCropsHooks.useFieldCropsStatus();
  const fieldEnrollmentsStatus =
    fieldEnrollmentsHooks.useFieldEnrollmentsStatus();

  return computeCombinedStatus(
    basicFieldsStatus,
    basicFarmsStatus,
    cropsStatus,
    fieldCropsStatus,
    fieldEnrollmentsStatus,
  );
};

// ####################
// ## useFieldsError ##
// ####################

export type ResultOfUseFieldsError = AxiosError | null;
export type UseFieldsError = () => ResultOfUseFieldsError;

/**
 * The returned value of this hook will contain the error
 * that has occurred in the fetching of the fields data
 * or null if no error occurred.
 */
export const useFieldsError: UseFieldsError = () => {
  const basicFieldsError = basicFieldsHooks.useBasicFieldsError();
  const basicFarmsError = basicFarmsHooks.useBasicFarmsError();
  const cropsError = cropsHooks.useCropsError();
  const fieldCropsError = fieldCropsHooks.useFieldCropsError();
  const fieldEnrollmentsError =
    fieldEnrollmentsHooks.useFieldEnrollmentsError();

  return computeCombinedError(
    basicFieldsError,
    basicFarmsError,
    cropsError,
    fieldCropsError,
    fieldEnrollmentsError,
  );
};

// ########################
// ## useMaybeFieldsData ##
// ########################

export type ResultOfUseMaybeFieldsData = FieldsDataEnvelope | undefined;
export type UseMaybeFieldsData = () => ResultOfUseMaybeFieldsData;

/**
 * The returned value of this hook will contain
 * the fields data, which may be undefined.
 */
export const useMaybeFieldsData: UseMaybeFieldsData = () => {
  const basicFieldsData = basicFieldsHooks.useMaybeBasicFieldsData();
  const basicFarmsData = basicFarmsHooks.useMaybeBasicFarmsData();
  const cropsData = cropsHooks.useMaybeCropsData();
  const fieldCropsData = fieldCropsHooks.useMaybeFieldCropsData();
  const fieldEnrollmentsData =
    fieldEnrollmentsHooks.useMaybeFieldEnrollmentsData();

  return computeCombinedData(
    basicFieldsData?.fields,
    basicFarmsData?.farmsById,
    cropsData?.cropsById,
    fieldCropsData?.fieldCropsByField,
    fieldEnrollmentsData?.fieldEnrollmentsByFeature,
  );
};

// ###################
// ## useFieldsData ##
// ###################

export type ResultOfUseFieldsData = FieldsDataEnvelope;
export type UseFieldsData = () => ResultOfUseFieldsData;

/**
 * The returned value of this hook will contain the fields data.
 *
 * This hook is guaranteed to contain fields data or will throw.
 */
export const useFieldsData: UseFieldsData = () => {
  const data = useMaybeFieldsData();
  if (data === undefined) {
    throw fieldsAccessError;
  }
  return data;
};

// ####################
// ## useMaybeFields ##
// ####################

export type ParamsOfUseMaybeFields = { forceDisable?: boolean };
export type ResultOfUseMaybeFields = QueryEnvelope<FieldsDataEnvelope>;
export type UseMaybeFields = (
  params?: ParamsOfUseMaybeFields,
) => ResultOfUseMaybeFields;

/**
 * The `data` property returned from this hook will contain
 * the fields data, which may be undefined
 *
 * The `error` and `status` properties returned from this hook
 * will reflect the state of the fields data.
 */
export const useMaybeFields: UseMaybeFields = ({ forceDisable } = {}) => {
  const basicFieldsEnvelope = basicFieldsHooks.useMaybeBasicFields({
    forceDisable,
  });
  const basicFarmsEnvelope = basicFarmsHooks.useMaybeBasicFarms({
    forceDisable,
  });
  const cropsEnvelope = cropsHooks.useMaybeCrops({ forceDisable });
  const fieldCropsEnvelope = fieldCropsHooks.useMaybeFieldCrops({
    forceDisable,
  });
  const fieldEnrollmentsEnvelope =
    fieldEnrollmentsHooks.useMaybeFieldEnrollments({ forceDisable });

  const setFieldsGlobalActivityAndStatus =
    useSetFieldsGlobalActivityAndStatus();

  const status = computeCombinedStatus(
    basicFieldsEnvelope.status,
    basicFarmsEnvelope.status,
    cropsEnvelope.status,
    fieldCropsEnvelope.status,
    fieldEnrollmentsEnvelope.status,
  );
  const error = computeCombinedError(
    basicFieldsEnvelope.error,
    basicFarmsEnvelope.error,
    cropsEnvelope.error,
    fieldCropsEnvelope.error,
    fieldEnrollmentsEnvelope.error,
  );
  const data = computeCombinedData(
    basicFieldsEnvelope.data?.fields,
    basicFarmsEnvelope.data?.farmsById,
    cropsEnvelope.data?.cropsById,
    fieldCropsEnvelope.data?.fieldCropsByField,
    fieldEnrollmentsEnvelope.data?.fieldEnrollmentsByFeature,
  );

  useHandleStatusForGlobalStatus({ status, setFieldsGlobalActivityAndStatus });

  return computeEnvelopeOfEnsuredState(data, status, error);
};

// ###############
// ## useFields ##
// ###############

export type ResultOfUseFields = EnsuredQueryEnvelope<FieldsDataEnvelope>;
export type UseFields = () => ResultOfUseFields;

/**
 * The `data` property returned from this hook will contain
 * the fields data.
 *
 * The `error` and `status` properties returned from this hook
 * will reflect the state of the fields data.
 *
 * This hook is guaranteed to contain fields data or will throw.
 */
export const useFields: UseFields = () => {
  const { data, status, error } = useMaybeFields();

  if (data === undefined) {
    throw fieldsAccessError;
  }

  return computeEnvelopeOfEnsuredStateWithData(data, status, error);
};
